import React from "react";
import {
  AbsoluteFill,
  Audio,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  AlertTriangle,
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  Bot,
  Brain,
  CheckCircle2,
  Clock,
  Cpu,
  Flame,
  Globe,
  Layers,
  MessageSquare,
  Network,
  PenTool,
  Search,
  Send,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Terminal,
  Users,
  XCircle,
  Zap,
} from "lucide-react";
import { DynamicSubtitle, SubtitleItem } from "./components/DynamicSubtitle";

// 15 Timed Subtitles calibrated with BOZ voiceover
const SUBTITLES: SubtitleItem[] = [
  {
    from: 0,
    to: 104,
    text: "Kalo selama ini lo sering capek ngerjain semua sendiri...",
    highlight: "capek ngerjain semua sendiri",
  },
  {
    from: 104,
    to: 228,
    text: "...mulai dari nyari ide, nulis, sampai beresin tugasnya,",
    highlight: "nyari ide, nulis, beresin tugas",
  },
  {
    from: 228,
    to: 345,
    text: "sekarang lo udah bisa bikin TIM KERJA SENDIRI pake AI!",
    highlight: "TIM KERJA SENDIRI pake AI",
  },
  {
    from: 355,
    to: 485,
    text: "Kuncinya: JANGAN pake 1 AI buat ngerjain semua urusan.",
    highlight: "JANGAN pake 1 AI",
  },
  {
    from: 495,
    to: 635,
    text: "Di laptop lo, lo bisa bikin beberapa asisten AI dengan tugas beda-beda.",
    highlight: "asisten AI dengan tugas beda-beda",
  },
  {
    from: 645,
    to: 725,
    text: "Ada yang khusus nyari info dan tren di internet...",
    highlight: "nyari info dan tren",
  },
  {
    from: 725,
    to: 795,
    text: "...ada yang khusus nulis kata-kata yang menarik...",
    highlight: "nulis kata-kata yang menarik",
  },
  {
    from: 795,
    to: 920,
    text: "...dan ada yang langsung nyelesaiin tugasnya di satu grup chat bareng lo!",
    highlight: "di satu grup chat bareng lo",
  },
  {
    from: 930,
    to: 1090,
    text: "Dan tenang aja, di sini kita SAMA SEKALI NGGAK jualan kelas atau e-course.",
    highlight: "NGGAK jualan kelas atau e-course",
  },
  {
    from: 1090,
    to: 1240,
    text: "Niat kita murni ngebangun komunitas buat saling belajar & bantu orang awam...",
    highlight: "saling belajar & bantu orang awam",
  },
  {
    from: 1240,
    to: 1325,
    text: "...biar nggak ketinggalan sama perkembangan AI sekarang.",
    highlight: "nggak ketinggalan",
  },
  {
    from: 1335,
    to: 1465,
    text: "Makanya, kita udah bikin GRUP KOMUNITAS AI HERMES di Telegram...",
    highlight: "GRUP KOMUNITAS AI HERMES",
  },
  {
    from: 1465,
    to: 1585,
    text: "...biar kita semua bisa sharing, diskusi, dan ngobrol santai bareng.",
    highlight: "sharing, diskusi, ngobrol santai",
  },
  {
    from: 1590,
    to: 1685,
    text: "Kalo lo pengen belajar bareng tanpa perlu bayar kelas mahal...",
    highlight: "tanpa perlu bayar kelas mahal",
  },
  {
    from: 1685,
    to: 1795,
    text: "...langsung aja KLIK LINK GRUP TELEGRAM kita di BIO ya!",
    highlight: "KLIK LINK GRUP TELEGRAM di BIO",
  },
];

export const HermesTeamCompanyExplainer: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Color Palette: Neo-Brutalist Streetwear & Swiss Editorial (No generic cyber neon grid!)
  const bgMain = "#090F0C"; // Deep Matte Pine Charcoal
  const paperCream = "#F6F4EE"; // Clean Swiss Editorial Paper
  const acidLime = "#D8FF3E"; // High-Energy Streetwear Acid
  const signalOrange = "#FF6B35";
  const alertRed = "#FF4B4B";
  const cyanBlue = "#38BDF8";
  const softMuted = "#D6DFD9"; // Crisp high-legibility light silver-gray
  const darkCard = "#111B15";
  const darkCard2 = "#16231C";
  const brutalShadow = "10px 10px 0px #000000";

  // Calibrated Spring Helper
  const sp = (delay: number, damping = 14, stiffness = 160) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 1 },
    });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgMain,
        color: paperCream,
        fontFamily: "'Rajdhani', sans-serif",
        overflow: "hidden",
      }}
    >
      {/* 1. Primary Mastered Voiceover Track (-16 LUFS) */}
      <Audio
        src={staticFile("ai_agent_assets/audio/hermes_team_vo_mastered.mp3")}
        volume={1.0}
      />

      {/* 2. Background Music Ducked (-24dB / volume 0.065) */}
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.065} loop />

      {/* 3. Dynamic SFX Cues */}
      {frame === 5 && <Audio src={staticFile("ui-pop.mp3")} volume={0.6} />}
      {frame === 110 && <Audio src={staticFile("swoosh-quick.mp3")} volume={0.5} />}
      {frame === 230 && <Audio src={staticFile("chime.mp3")} volume={0.6} />}
      {frame === 360 && <Audio src={staticFile("sfx_error_buzzer.mp3")} volume={0.35} />}
      {frame === 490 && <Audio src={staticFile("ui-pop.mp3")} volume={0.5} />}
      {frame === 650 && <Audio src={staticFile("click.mp3")} volume={0.5} />}
      {frame === 800 && <Audio src={staticFile("ui-success.mp3")} volume={0.6} />}
      {frame === 935 && <Audio src={staticFile("sfx_error_buzzer.mp3")} volume={0.3} />}
      {frame === 1100 && <Audio src={staticFile("chime.mp3")} volume={0.65} />}
      {frame === 1340 && <Audio src={staticFile("ui-pop.mp3")} volume={0.6} />}
      {frame === 1595 && <Audio src={staticFile("chime.mp3")} volume={0.8} />}

      {/* 1. Restored Top Duration & Progress Bar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 8,
          backgroundColor: "rgba(255, 255, 255, 0.08)",
          zIndex: 999,
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${interpolate(frame, [0, 1800], [0, 100], {
              extrapolateRight: "clamp",
            })}%`,
            backgroundColor: acidLime,
            boxShadow: `0 0 12px ${acidLime}`,
          }}
        />
      </div>

      {/* ========================================================================= */}
      {/* SCENE 1: FRAMES 0 - 350 (0.00s - 11.67s) */}
      {/* BURNOUT SENDIRIAN VS TIM AI PRIBADI — PERFECTLY BALANCED STAGE */}
      {/* ========================================================================= */}
      {frame >= 0 && frame < 350 && (
        <div
          style={{
            position: "absolute",
            top: 140,
            bottom: 420,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: 940,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 24,
            }}
          >
            {/* Top Label Centered */}
            <div
              style={{
                transform: `scale(${sp(0)})`,
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                backgroundColor: "rgba(255, 75, 75, 0.15)",
                border: "2px solid #FF4B4B",
                color: "#FF4B4B",
                padding: "12px 26px",
                borderRadius: 18,
                fontSize: 24,
                fontWeight: 900,
                letterSpacing: "0.04em",
              }}
            >
              <AlertTriangle size={26} color="#FF4B4B" />
              <span>FRIKSI SOLO WORKER</span>
            </div>

            {/* Massive Display Title Centered */}
            <h1
              style={{
                fontFamily: "'Anton', sans-serif",
                fontSize: 92,
                lineHeight: 0.95,
                margin: 0,
                color: "#FFFFFF",
                transform: `translateY(${interpolate(sp(5), [0, 1], [40, 0])}px)`,
                opacity: sp(5),
              }}
            >
              STOP KERJA RODI <br />
              <span style={{ color: acidLime }}>SENDIRIAN!</span>
            </h1>

            {/* Card 1: Cara Lama (Solo Burnout) */}
            <div
              style={{
                width: "100%",
                backgroundColor: darkCard,
                borderRadius: 28,
                border: "3px solid rgba(255, 75, 75, 0.55)",
                padding: "30px 36px",
                boxShadow: brutalShadow,
                transform: `scale(${sp(15)})`,
                display: "flex",
                flexDirection: "column",
                gap: 22,
                textAlign: "left",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <XCircle size={38} color="#FF4B4B" />
                  <span style={{ fontSize: 30, fontWeight: 900, color: "#FF4B4B" }}>
                    CARA LAMA: 1 ORANG KERJA PALUGADA
                  </span>
                </div>
                <span
                  style={{
                    backgroundColor: "#FF4B4B",
                    color: "#FFFFFF",
                    fontSize: 20,
                    fontWeight: 900,
                    padding: "8px 20px",
                    borderRadius: 10,
                  }}
                >
                  BURNOUT
                </span>
              </div>

              {/* Grid 4 Tasks with LARGER TEXT */}
              <div style={{ display: "flex", gap: 14 }}>
                {[
                  { label: "Mikir Ide", sub: "Buntet & Pusing" },
                  { label: "Nulis Copy", sub: "Buntu Kalimat" },
                  { label: "Koding", sub: "Error Sendirian" },
                  { label: "Beresin Tugas", sub: "Kurang Tidur" },
                ].map((task, idx) => (
                  <div
                    key={idx}
                    style={{
                      flex: 1,
                      backgroundColor: "rgba(255, 255, 255, 0.06)",
                      border: "1px solid rgba(255, 255, 255, 0.14)",
                      borderRadius: 20,
                      padding: "24px 12px",
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                    }}
                  >
                    <span style={{ fontSize: 27, fontWeight: 900, color: "#FFFFFF" }}>
                      {task.label}
                    </span>
                    <span style={{ fontSize: 19, fontWeight: 800, color: alertRed }}>
                      {task.sub}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Down Arrow Indicator with Lucide Icon */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                transform: `scale(${sp(45)})`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  backgroundColor: acidLime,
                  color: "#0B120E",
                  padding: "14px 36px",
                  borderRadius: 30,
                  fontSize: 24,
                  fontWeight: 900,
                  boxShadow: "5px 5px 0px #000000",
                }}
              >
                <span>SOLUSI BARU BARENG HERMES</span>
                <ArrowDown size={28} color="#0B120E" />
              </div>
            </div>

            {/* Card 2: Cara Baru (Tim AI Otonom) */}
            <div
              style={{
                width: "100%",
                backgroundColor: darkCard2,
                borderRadius: 28,
                border: `3px solid ${acidLime}`,
                padding: "30px 36px",
                boxShadow: brutalShadow,
                transform: `scale(${sp(60)})`,
                display: "flex",
                flexDirection: "column",
                gap: 22,
                textAlign: "left",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <CheckCircle2 size={38} color={acidLime} />
                  <span style={{ fontSize: 30, fontWeight: 900, color: "#FFFFFF" }}>
                    CARA BARU: BIKIN TIM AI SENDIRI
                  </span>
                </div>
                <span
                  style={{
                    backgroundColor: acidLime,
                    color: "#0B120E",
                    fontSize: 20,
                    fontWeight: 900,
                    padding: "8px 20px",
                    borderRadius: 10,
                  }}
                >
                  FULL OTONOM
                </span>
              </div>

              {/* Roles Badges with LARGER TEXT */}
              <div style={{ display: "flex", gap: 16 }}>
                {[
                  { name: "BOT 01: INTEL", role: "Riset Tren Web", color: cyanBlue },
                  { name: "BOT 02: KREATIF", role: "Nulis Naskah", color: acidLime },
                  { name: "BOT 03: EKSEKUSI", role: "Tuntasin Tugas", color: signalOrange },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      flex: 1,
                      backgroundColor: "#0C1711",
                      border: `2px solid ${item.color}`,
                      borderRadius: 22,
                      padding: "24px 16px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span
                      style={{ fontSize: 26, fontWeight: 900, color: item.color }}
                    >
                      {item.name}
                    </span>
                    <span
                      style={{ fontSize: 22, fontWeight: 800, color: "#FFFFFF" }}
                    >
                      {item.role}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom Synergy Card */}
            <div
              style={{
                width: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.45)",
                borderRadius: 24,
                border: "2px dashed rgba(255, 255, 255, 0.22)",
                padding: "24px 32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                transform: `scale(${sp(120)})`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <Zap size={32} color={acidLime} />
                <span style={{ fontSize: 25, fontWeight: 800, color: "#FFFFFF" }}>
                  Output Setara 1 Kantor Penuh Hanya Dari Laptop Lo Sendiri
                </span>
              </div>
              <span style={{ fontSize: 23, fontWeight: 900, color: acidLime }}>
                100% PRIVATE
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 2: FRAMES 350 - 485 (11.67s - 16.17s) */}
      {/* KUNCINYA: JANGAN PAKE 1 AI BUAT SEMUA URUSAN — PERFECTLY CENTERED */}
      {/* ========================================================================= */}
      {frame >= 350 && frame < 485 && (
        <div
          style={{
            position: "absolute",
            top: 140,
            bottom: 420,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: 940,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 28,
            }}
          >
            {/* Warning Pill */}
            <div
              style={{
                transform: `scale(${sp(355)})`,
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                backgroundColor: "rgba(255, 107, 53, 0.15)",
                border: "2px solid #FF6B35",
                color: "#FF6B35",
                padding: "12px 26px",
                borderRadius: 18,
                fontSize: 24,
                fontWeight: 900,
                letterSpacing: "0.04em",
              }}
            >
              <AlertTriangle size={26} color="#FF6B35" />
              <span>PRINSIP PALING PENTING</span>
            </div>

            {/* Title Centered */}
            <h1
              style={{
                fontFamily: "'Anton', sans-serif",
                fontSize: 92,
                lineHeight: 0.95,
                margin: 0,
                color: "#FFFFFF",
                transform: `translateY(${interpolate(sp(360), [0, 1], [40, 0])}px)`,
                opacity: sp(360),
              }}
            >
              JANGAN PAKE <span style={{ color: alertRed }}>1 AI</span> <br />
              UNTUK SEMUA URUSAN!
            </h1>

            {/* Overload Visual Card */}
            <div
              style={{
                width: "100%",
                backgroundColor: darkCard,
                borderRadius: 30,
                border: "3px solid rgba(255, 75, 75, 0.55)",
                padding: "40px 44px",
                boxShadow: brutalShadow,
                transform: `scale(${sp(370)})`,
                display: "flex",
                flexDirection: "column",
                gap: 26,
                textAlign: "left",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                  <ShieldAlert size={56} color="#FF6B35" />
                  <div>
                    <div style={{ fontSize: 34, fontWeight: 900, color: "#FFFFFF" }}>
                      AI PALUGADA = HASIL SETENGAH MATANG
                    </div>
                    <div style={{ fontSize: 25, color: softMuted, fontWeight: 700 }}>
                      Gak ada manusia yang jago 10 profesi sekaligus. AI juga sama!
                    </div>
                  </div>
                </div>
              </div>

              {/* The Mistake Breakdown with LARGER TEXT */}
              <div
                style={{
                  backgroundColor: "rgba(0, 0, 0, 0.45)",
                  borderRadius: 24,
                  padding: "30px 36px",
                  border: "2px dashed rgba(255, 75, 75, 0.45)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 20,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: 27,
                    fontWeight: 800,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <XCircle size={30} color="#FF4B4B" />
                    <span style={{ color: "#FF4B4B" }}>1 Chatbot Generik:</span>
                  </div>
                  <span style={{ color: paperCream }}>
                    Prompt kepanjangan, gampang lupa & halusinasi
                  </span>
                </div>
                <div
                  style={{
                    height: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.14)",
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: 27,
                    fontWeight: 800,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <CheckCircle2 size={30} color={acidLime} />
                    <span style={{ color: acidLime }}>Solusi Hermes:</span>
                  </div>
                  <span style={{ color: "#FFFFFF" }}>
                    Bagi jadi beberapa agen spesialis terisolasi!
                  </span>
                </div>
              </div>
            </div>

            {/* Bottom Supporting Card */}
            <div
              style={{
                width: "100%",
                backgroundColor: darkCard2,
                borderRadius: 26,
                border: "2px solid rgba(255, 255, 255, 0.18)",
                padding: "28px 36px",
                display: "flex",
                alignItems: "center",
                gap: 20,
                transform: `scale(${sp(410)})`,
                textAlign: "left",
              }}
            >
              <Cpu size={44} color={acidLime} />
              <div style={{ fontSize: 25, fontWeight: 700, color: paperCream }}>
                Tiap bot di Hermes punya memori, tools, dan kepribadian sendiri yang
                fokus ke 1 keahlian!
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 3: FRAMES 485 - 925 (16.17s - 30.83s) */}
      {/* 3 ASISTEN SPESIALIS DI SATU GRUP CHAT — PERFECTLY BALANCED STAGE */}
      {/* ========================================================================= */}
      {frame >= 485 && frame < 925 && (
        <div
          style={{
            position: "absolute",
            top: 140,
            bottom: 420,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: 940,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 20,
            }}
          >
            {/* Header Badge */}
            <div
              style={{
                transform: `scale(${sp(490)})`,
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                backgroundColor: "rgba(216, 255, 62, 0.15)",
                border: `2px solid ${acidLime}`,
                color: acidLime,
                padding: "12px 26px",
                borderRadius: 18,
                fontSize: 24,
                fontWeight: 900,
                letterSpacing: "0.04em",
              }}
            >
              <Layers size={26} color={acidLime} />
              <span>STRUKTUR KANTOR VIRTUAL</span>
            </div>

            {/* Headline */}
            <h1
              style={{
                fontFamily: "'Anton', sans-serif",
                fontSize: 90,
                lineHeight: 0.95,
                margin: 0,
                color: "#FFFFFF",
                transform: `translateY(${interpolate(sp(495), [0, 1], [40, 0])}px)`,
                opacity: sp(495),
              }}
            >
              3 ASISTEN SPESIALIS <br />
              <span style={{ color: acidLime }}>DI 1 GRUP CHAT!</span>
            </h1>

            {/* 3 Bento Cards with LARGER TEXT & GENEROUS PADDING */}
            <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Agent 1: Intel & Riset */}
              <div
                style={{
                  backgroundColor: darkCard,
                  borderRadius: 24,
                  border: `3px solid ${
                    frame >= 645 && frame < 725 ? cyanBlue : "rgba(255,255,255,0.16)"
                  }`,
                  padding: "24px 32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  boxShadow: "6px 6px 0px #000000",
                  transform: `scale(${sp(510)})`,
                  textAlign: "left",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
                  <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: 20,
                      backgroundColor: "rgba(56, 189, 248, 0.16)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Search size={42} color={cyanBlue} />
                  </div>
                  <div>
                    <div style={{ fontSize: 31, fontWeight: 900, color: "#FFFFFF" }}>
                      BOT 01: INTEL & RISET TREN
                    </div>
                    <div style={{ fontSize: 23, color: softMuted, fontWeight: 700 }}>
                      Khusus pantau internet, data, dan kompetitor 24 jam
                    </div>
                  </div>
                </div>
                <span
                  style={{
                    backgroundColor: cyanBlue,
                    color: "#0B120E",
                    fontSize: 21,
                    fontWeight: 900,
                    padding: "10px 20px",
                    borderRadius: 14,
                  }}
                >
                  ONLINE 24/7
                </span>
              </div>

              {/* Agent 2: Copywriter & Kreatif */}
              <div
                style={{
                  backgroundColor: darkCard,
                  borderRadius: 24,
                  border: `3px solid ${
                    frame >= 725 && frame < 795 ? acidLime : "rgba(255,255,255,0.16)"
                  }`,
                  padding: "24px 32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  boxShadow: "6px 6px 0px #000000",
                  transform: `scale(${sp(530)})`,
                  textAlign: "left",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
                  <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: 20,
                      backgroundColor: "rgba(216, 255, 62, 0.16)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PenTool size={42} color={acidLime} />
                  </div>
                  <div>
                    <div style={{ fontSize: 31, fontWeight: 900, color: "#FFFFFF" }}>
                      BOT 02: KREATIF & COPYWRITER
                    </div>
                    <div style={{ fontSize: 23, color: softMuted, fontWeight: 700 }}>
                      Khusus nulis kata-kata jualan & naskah yang menarik
                    </div>
                  </div>
                </div>
                <span
                  style={{
                    backgroundColor: acidLime,
                    color: "#0B120E",
                    fontSize: 21,
                    fontWeight: 900,
                    padding: "10px 20px",
                    borderRadius: 14,
                  }}
                >
                  HOOK VIRAL
                </span>
              </div>

              {/* Agent 3: Eksekusi Otonom */}
              <div
                style={{
                  backgroundColor: darkCard,
                  borderRadius: 24,
                  border: `3px solid ${
                    frame >= 795 ? signalOrange : "rgba(255,255,255,0.16)"
                  }`,
                  padding: "24px 32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  boxShadow: "6px 6px 0px #000000",
                  transform: `scale(${sp(550)})`,
                  textAlign: "left",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
                  <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: 20,
                      backgroundColor: "rgba(249, 115, 22, 0.16)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Terminal size={42} color={signalOrange} />
                  </div>
                  <div>
                    <div style={{ fontSize: 31, fontWeight: 900, color: "#FFFFFF" }}>
                      BOT 03: EKSEKUSI OTONOM
                    </div>
                    <div style={{ fontSize: 23, color: softMuted, fontWeight: 700 }}>
                      Langsung beresin tugas teknis di laptop sampai tuntas
                    </div>
                  </div>
                </div>
                <span
                  style={{
                    backgroundColor: signalOrange,
                    color: "#0B120E",
                    fontSize: 21,
                    fontWeight: 900,
                    padding: "10px 20px",
                    borderRadius: 14,
                  }}
                >
                  LANGSUNG JALAN
                </span>
              </div>
            </div>

            {/* Chat Room Synergy Mockup with LARGER TEXT & DEEPER VERTICAL PRESENCE */}
            <div
              style={{
                width: "100%",
                backgroundColor: "rgba(10, 16, 13, 0.96)",
                borderRadius: 26,
                border: "2px dashed rgba(216, 255, 62, 0.45)",
                padding: "26px 32px",
                display: "flex",
                flexDirection: "column",
                gap: 14,
                transform: `scale(${sp(580)})`,
                textAlign: "left",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <MessageSquare size={30} color={acidLime} />
                  <span style={{ fontSize: 25, fontWeight: 900, color: "#FFFFFF" }}>
                    ALUR ESTAFET DI GRUP TELEGRAM / DISCORD
                  </span>
                </div>
                <span
                  style={{
                    backgroundColor: acidLime,
                    color: "#0B120E",
                    fontSize: 18,
                    fontWeight: 900,
                    padding: "6px 16px",
                    borderRadius: 8,
                  }}
                >
                  AUTO-SYNC
                </span>
              </div>

              {/* Mini dialogue bubbles with LARGER TEXT */}
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                    padding: "14px 22px",
                    borderRadius: 16,
                    fontSize: 23,
                    fontWeight: 700,
                    color: paperCream,
                  }}
                >
                  <span style={{ color: acidLime, fontWeight: 900 }}>Lo (CEO):</span> "Tim,
                  cariin tren video AI hari ini terus bikinin naskahnya."
                </div>
                <div
                  style={{
                    backgroundColor: "rgba(56, 189, 248, 0.12)",
                    padding: "14px 22px",
                    borderRadius: 16,
                    fontSize: 23,
                    fontWeight: 700,
                    color: cyanBlue,
                  }}
                >
                  <span style={{ fontWeight: 900 }}>Bot 01 (Intel):</span> "3 topik viral
                  berhasil ditarik dari Twitter & Reddit."
                </div>
                <div
                  style={{
                    backgroundColor: "rgba(216, 255, 62, 0.12)",
                    padding: "14px 22px",
                    borderRadius: 16,
                    fontSize: 23,
                    fontWeight: 700,
                    color: acidLime,
                  }}
                >
                  <span style={{ fontWeight: 900 }}>Bot 02 (Kreatif):</span> "Naskah 60 detik
                  udah siap dibaca, link di bio."
                </div>
                <div
                  style={{
                    backgroundColor: "rgba(249, 115, 22, 0.12)",
                    padding: "14px 22px",
                    borderRadius: 16,
                    fontSize: 23,
                    fontWeight: 700,
                    color: signalOrange,
                  }}
                >
                  <span style={{ fontWeight: 900 }}>Bot 03 (Eksekusi):</span> "Otomatis di-render
                  di Remotion & siap dipublish!"
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 4: FRAMES 925 - 1330 (30.83s - 44.33s) */}
      {/* KITA NGGAK JUALAN KELAS / ECOURSE — PERFECTLY BALANCED STAGE */}
      {/* ========================================================================= */}
      {frame >= 925 && frame < 1330 && (
        <div
          style={{
            position: "absolute",
            top: 140,
            bottom: 420,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: 940,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 24,
            }}
          >
            {/* Stamp Tag with Lucide */}
            <div
              style={{
                transform: `scale(${sp(930)}) rotate(-2deg)`,
                display: "inline-flex",
                alignItems: "center",
                gap: 14,
                backgroundColor: alertRed,
                color: "#FFFFFF",
                padding: "14px 28px",
                borderRadius: 18,
                fontSize: 26,
                fontWeight: 900,
                letterSpacing: "0.06em",
                boxShadow: "6px 6px 0px #000000",
              }}
            >
              <XCircle size={30} color="#FFFFFF" />
              <span>ZERO GIMMICK · 0% JUALAN KELAS</span>
            </div>

            {/* Display Headline */}
            <h1
              style={{
                fontFamily: "'Anton', sans-serif",
                fontSize: 90,
                lineHeight: 0.95,
                margin: 0,
                color: "#FFFFFF",
                transform: `translateY(${interpolate(sp(935), [0, 1], [40, 0])}px)`,
                opacity: sp(935),
              }}
            >
              KITA BANGUN RUANG <br />
              <span style={{ color: acidLime }}>BELAJAR GRATIS!</span>
            </h1>

            {/* Swiss Editorial Clean Card */}
            <div
              style={{
                width: "100%",
                backgroundColor: paperCream,
                color: "#0B120E",
                borderRadius: 30,
                padding: "40px 44px",
                boxShadow: brutalShadow,
                transform: `scale(${sp(945)})`,
                display: "flex",
                flexDirection: "column",
                gap: 24,
                textAlign: "left",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "2px solid rgba(0,0,0,0.12)",
                  paddingBottom: 20,
                }}
              >
                <span
                  style={{
                    fontSize: 27,
                    fontWeight: 900,
                    letterSpacing: "0.05em",
                    color: "#0B120E",
                  }}
                >
                  KOMITMEN KOMUNITAS
                </span>
                <span
                  style={{
                    backgroundColor: "#0B120E",
                    color: acidLime,
                    padding: "8px 20px",
                    borderRadius: 12,
                    fontSize: 21,
                    fontWeight: 900,
                  }}
                >
                  100% BEBAS BIAYA
                </span>
              </div>

              {/* Pillars with LARGER TEXT */}
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {[
                  "Nggak ada tawaran kelas mahal atau ecourse berbayar",
                  "Murni ngebantu orang awam biar paham teknologi AI",
                  "Saling sharing ilmu, eksperimen, & praktek nyata",
                ].map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 20,
                      fontSize: 27,
                      fontWeight: 800,
                      color: "#18251F",
                    }}
                  >
                    <CheckCircle2 size={36} color="#0D8A4E" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              {/* Bottom Quote Banner */}
              <div
                style={{
                  backgroundColor: "#E5E0D5",
                  borderRadius: 20,
                  padding: "22px 28px",
                  fontSize: 24,
                  fontWeight: 700,
                  color: "#334139",
                  fontStyle: "italic",
                }}
              >
                "Teknologi secanggih ini harusnya bikin kita semua mandiri, bukan malah
                dijadiin ladang jualan yang bikin pusing."
              </div>
            </div>

            {/* Target Audience Pill */}
            <div
              style={{
                width: "100%",
                backgroundColor: darkCard,
                borderRadius: 24,
                border: "2px solid rgba(255, 255, 255, 0.18)",
                padding: "22px 30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                transform: `scale(${sp(1120)})`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <Users size={32} color={acidLime} />
                <span style={{ fontSize: 25, fontWeight: 800, color: "#FFFFFF" }}>
                  Terbuka untuk siapa aja: Freelancer, Mahasiswa, Pebisnis, & Kreator
                </span>
              </div>
              <span style={{ fontSize: 23, fontWeight: 900, color: acidLime }}>
                SEMUA BISA
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 5: FRAMES 1330 - 1590 (44.33s - 53.00s) */}
      {/* GRUP KOMUNITAS TELEGRAM AI HERMES — PERFECTLY BALANCED STAGE */}
      {/* ========================================================================= */}
      {frame >= 1330 && frame < 1590 && (
        <div
          style={{
            position: "absolute",
            top: 140,
            bottom: 420,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: 940,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 24,
            }}
          >
            {/* Telegram Hub Tag with Lucide */}
            <div
              style={{
                transform: `scale(${sp(1335)})`,
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                backgroundColor: "rgba(56, 189, 248, 0.15)",
                border: "2px solid #38BDF8",
                color: "#38BDF8",
                padding: "12px 26px",
                borderRadius: 18,
                fontSize: 24,
                fontWeight: 900,
                letterSpacing: "0.04em",
              }}
            >
              <Send size={26} color="#38BDF8" />
              <span>WADAH DISKUSI TERBUKA</span>
            </div>

            {/* Display Headline */}
            <h1
              style={{
                fontFamily: "'Anton', sans-serif",
                fontSize: 90,
                lineHeight: 0.95,
                margin: 0,
                color: "#FFFFFF",
                transform: `translateY(${interpolate(sp(1340), [0, 1], [40, 0])}px)`,
                opacity: sp(1340),
              }}
            >
              GRUP KOMUNITAS <br />
              <span style={{ color: acidLime }}>AI HERMES DI TELEGRAM</span>
            </h1>

            {/* Telegram Hero Card */}
            <div
              style={{
                width: "100%",
                backgroundColor: darkCard,
                borderRadius: 30,
                border: "3px solid #38BDF8",
                padding: "36px 40px",
                boxShadow: brutalShadow,
                transform: `scale(${sp(1350)})`,
                display: "flex",
                flexDirection: "column",
                gap: 26,
                textAlign: "left",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                  <div
                    style={{
                      width: 76,
                      height: 76,
                      borderRadius: 22,
                      backgroundColor: "#38BDF8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Send size={44} color="#0B120E" />
                  </div>
                  <div>
                    <div style={{ fontSize: 34, fontWeight: 900, color: "#FFFFFF" }}>
                      AI HERMES INDONESIA
                    </div>
                    <div style={{ fontSize: 23, color: softMuted, fontWeight: 700 }}>
                      Grup Komunitas Terbuka · Telegram
                    </div>
                  </div>
                </div>
                <span
                  style={{
                    backgroundColor: "rgba(56, 189, 248, 0.2)",
                    color: "#38BDF8",
                    fontSize: 21,
                    fontWeight: 900,
                    padding: "10px 22px",
                    borderRadius: 14,
                    border: "1px solid #38BDF8",
                  }}
                >
                  FREE JOIN
                </span>
              </div>

              {/* 3 Activities in Telegram with LARGER TEXT */}
              <div style={{ display: "flex", gap: 18 }}>
                {[
                  { title: "SHARING", desc: "Tukar ilmu & workflow", icon: Sparkles },
                  { title: "DISKUSI", desc: "Tanya jawab santai", icon: MessageSquare },
                  { title: "NGOBLAS", desc: "Gak pake kaku / gengsi", icon: Users },
                ].map((act, idx) => {
                  const IconComp = act.icon;
                  return (
                    <div
                      key={idx}
                      style={{
                        flex: 1,
                        backgroundColor: "rgba(255, 255, 255, 0.06)",
                        border: "1px solid rgba(255, 255, 255, 0.14)",
                        borderRadius: 24,
                        padding: "26px 18px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 12,
                        textAlign: "center",
                      }}
                    >
                      <IconComp size={44} color={acidLime} />
                      <span
                        style={{ fontSize: 28, fontWeight: 900, color: "#FFFFFF" }}
                      >
                        {act.title}
                      </span>
                      <span
                        style={{ fontSize: 20, fontWeight: 800, color: softMuted }}
                      >
                        {act.desc}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Telegram Feature Highlight */}
            <div
              style={{
                width: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                borderRadius: 24,
                border: "2px dashed rgba(56, 189, 248, 0.45)",
                padding: "24px 32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                transform: `scale(${sp(1470)})`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <Clock size={32} color={cyanBlue} />
                <span style={{ fontSize: 25, fontWeight: 800, color: "#FFFFFF" }}>
                  Sharing Setup Hermes, Multi-Agent Profiles, & Script Siap Pakai!
                </span>
              </div>
              <span style={{ fontSize: 23, fontWeight: 900, color: cyanBlue }}>
                AKTIF TIAP HARI
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 6: FRAMES 1590 - 1800 (53.00s - 60.00s) */}
      {/* CALL TO ACTION: KLIK LINK DI BIO YA — PERFECTLY BALANCED STAGE */}
      {/* ========================================================================= */}
      {frame >= 1590 && (
        <div
          style={{
            position: "absolute",
            top: 140,
            bottom: 420,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: 940,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 26,
            }}
          >
            {/* CTA Tag with Lucide */}
            <div
              style={{
                transform: `scale(${sp(1595)})`,
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                backgroundColor: acidLime,
                color: "#0B120E",
                padding: "14px 28px",
                borderRadius: 18,
                fontSize: 24,
                fontWeight: 900,
                letterSpacing: "0.05em",
                boxShadow: "5px 5px 0px #000000",
              }}
            >
              <Flame size={28} color="#0B120E" />
              <span>GABUNG SEKARANG JUGA</span>
            </div>

            {/* Display Headline */}
            <h1
              style={{
                fontFamily: "'Anton', sans-serif",
                fontSize: 92,
                lineHeight: 0.95,
                margin: 0,
                color: "#FFFFFF",
                transform: `translateY(${interpolate(sp(1600), [0, 1], [40, 0])}px)`,
                opacity: sp(1600),
              }}
            >
              BELAJAR BARENG <br />
              <span style={{ color: acidLime }}>KLIK LINK DI BIO!</span>
            </h1>

            {/* Giant Action Box with LARGER TEXT */}
            <div
              style={{
                width: "100%",
                backgroundColor: acidLime,
                color: "#0B120E",
                borderRadius: 34,
                padding: "44px 44px",
                boxShadow: "12px 12px 0px #000000",
                transform: `scale(${sp(1610)})`,
                display: "flex",
                flexDirection: "column",
                gap: 28,
                border: "4px solid #000000",
                textAlign: "left",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
                  <div
                    style={{
                      width: 82,
                      height: 82,
                      borderRadius: 24,
                      backgroundColor: "#0B120E",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Send size={48} color={acidLime} />
                  </div>
                  <div>
                    <div style={{ fontSize: 40, fontWeight: 900 }}>
                      GRUP TELEGRAM AI HERMES
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: "#18251F" }}>
                      Link langsung tersedia di profil bio kita!
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    width: 68,
                    height: 68,
                    borderRadius: "50%",
                    backgroundColor: "#0B120E",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ArrowUpRight size={44} color={acidLime} />
                </div>
              </div>

              {/* Bottom Guarantee Banner */}
              <div
                style={{
                  backgroundColor: "#0B120E",
                  color: "#FFFFFF",
                  borderRadius: 22,
                  padding: "22px 30px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <CheckCircle2 size={28} color={acidLime} />
                  <span style={{ fontSize: 25, fontWeight: 800 }}>
                    Tanpa Bayar Kelas Mahal
                  </span>
                </div>
                <span style={{ fontSize: 25, fontWeight: 800, color: acidLime }}>
                  100% Terbuka Untuk Umum
                </span>
              </div>
            </div>

            {/* Pointer / Swipe Up Reminder */}
            <div
              style={{
                width: "100%",
                backgroundColor: darkCard,
                borderRadius: 24,
                border: "2px solid rgba(255, 255, 255, 0.18)",
                padding: "22px 30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 16,
                transform: `scale(${sp(1680)})`,
              }}
            >
              <ArrowUpRight size={30} color={acidLime} />
              <span style={{ fontSize: 25, fontWeight: 800, color: "#FFFFFF" }}>
                Tinggal buka profil dan klik link Telegram di bio ya!
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 4. DYNAMIC SUBTITLE ENGINE (Y: 1540px / bottom: 380px SAFE BAND) */}
      <DynamicSubtitle
        frame={frame}
        subtitles={SUBTITLES}
        bottom={380} // Perfectly anchored above TikTok bottom UI
        highlightColor={acidLime}
        backgroundColor="rgba(11, 18, 14, 0.96)"
        fontSize={32}
      />

      {/* 5. Clean Watermark Below Subtitle */}
      <div
        style={{
          position: "absolute",
          bottom: 310,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 90,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            backgroundColor: "rgba(11, 18, 14, 0.65)",
            padding: "8px 24px",
            borderRadius: 20,
            border: "1px solid rgba(255, 255, 255, 0.14)",
          }}
        >
          <span
            style={{
              fontFamily: "'Rajdhani', sans-serif",
              fontSize: 24,
              fontWeight: 800,
              letterSpacing: "0.08em",
              color: "rgba(255, 255, 255, 0.7)",
            }}
          >
            TikTok @kyuidn.co
          </span>
        </div>
      </div>
    </AbsoluteFill>
  );
};
